import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { ArrowRight, GraduationCap, Zap, Heart, Check, BookOpen } from 'lucide-react';
import { useState } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { CATEGORIES, getCategoryByTag } from '@/lib/categories';
import { useLatestArticles } from '@/hooks/useArticles';
import type { Article } from '@/lib/types';

function ArticleCard({ article }: { article: Article }) {
  const category = getCategoryByTag(article.category);
  const Icon = category?.icon;

  return (
    <Link to={`/artikel/${article.slug}`}>
      <Card className="group h-full overflow-hidden border-border/50 bg-card/50 backdrop-blur hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300">
        {article.image && (
          <div className="aspect-video overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
        )}
        <CardHeader className="pb-2">
          {category && (
            <div className="flex items-center gap-2 text-primary text-xs font-medium mb-2">
              {Icon && <Icon className="w-3.5 h-3.5" />}
              <span>{category.name}</span>
            </div>
          )}
          <CardTitle className="text-lg group-hover:text-primary transition-colors line-clamp-2">
            {article.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CardDescription className="line-clamp-2">{article.summary}</CardDescription>
          <div className="flex items-center gap-1 mt-4 text-sm text-primary font-medium">
            <span>Weiterlesen</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function ArticleCardSkeleton() {
  return (
    <Card className="h-full overflow-hidden border-border/50 bg-card/50">
      <Skeleton className="aspect-video" />
      <CardHeader className="pb-2">
        <Skeleton className="h-4 w-24 mb-2" />
        <Skeleton className="h-6 w-full" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-2/3" />
      </CardContent>
    </Card>
  );
}

const LIGHTNING_ADDRESS = 'wtfisbitcoin@minibits.cash';

function DonationSection() {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(LIGHTNING_ADDRESS);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = LIGHTNING_ADDRESS;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <section className="container pb-16">
      <Card className="border-border/50 bg-card/50 backdrop-blur overflow-hidden">
        <CardContent className="p-8 lg:p-12">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <div className="flex-shrink-0">
              <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center animate-glow">
                <Heart className="w-10 h-10 text-primary" />
              </div>
            </div>
            <div className="flex-1 text-center lg:text-left">
              <h2 className="text-2xl font-bold mb-3">
                Unterstütze wtfisBitcoin
              </h2>
              <p className="text-muted-foreground mb-4">
                Diese Lernplattform ist kostenlos und werbefrei. Wenn dir die Inhalte helfen, 
                freue ich mich über eine kleine Spende via Lightning.
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <button
                  onClick={copyToClipboard}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-all shadow-lg shadow-primary/25"
                >
                  {copied ? (
                    <>
                      <Check className="w-5 h-5" />
                      Kopiert!
                    </>
                  ) : (
                    <>
                      <Zap className="w-5 h-5" />
                      Lightning Adresse kopieren
                    </>
                  )}
                </button>
                <span className="text-sm text-muted-foreground font-mono bg-secondary/50 px-3 py-2 rounded-lg">
                  {LIGHTNING_ADDRESS}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

function CategoryCard({ category }: { category: typeof CATEGORIES[0] }) {
  const Icon = category.icon;

  return (
    <Link to={`/kategorie/${category.id}`}>
      <Card className="group h-full border-border/50 bg-card/50 backdrop-blur hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300">
        <CardContent className="p-6 flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
            <Icon className="w-7 h-7 text-primary" />
          </div>
          <h3 className="font-semibold text-lg mb-2 group-hover:text-primary transition-colors">
            {category.name}
          </h3>
          <p className="text-sm text-muted-foreground">{category.description}</p>
        </CardContent>
      </Card>
    </Link>
  );
}

const Index = () => {
  const { data: articles, isLoading } = useLatestArticles(3);

  useSeoMeta({
    title: 'wtfisBitcoin - What the Fuck is Bitcoin?',
    description: 'Dein Wegweiser durch die Welt von Bitcoin. Verstehe endlich Bitcoin, Lightning Network, Wallets und mehr.',
    ogImage: 'https://blossom.primal.net/dd3e0ea310123f58aeb8af1c04700c00ab0be4f59b336a1f6132f2a919e9250d.jpg',
    ogType: 'website',
    twitterCard: 'summary_large_image',
  });

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-background" />
        
        <div className="relative container py-12 lg:py-20">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
            {/* Logo Image */}
            <div className="flex-shrink-0 animate-fade-in">
              <img 
                src="/wtfisbitcoin-logo.jpg" 
                alt="What the Fuck is Bitcoin" 
                className="w-64 h-64 lg:w-80 lg:h-80 object-contain rounded-2xl shadow-2xl shadow-primary/20"
              />
            </div>
            
            {/* Text Content */}
            <div className="flex-1 text-center lg:text-left animate-fade-in" style={{ animationDelay: '100ms' }}>
              <div className="flex items-center justify-center lg:justify-start gap-2 text-primary font-medium mb-4">
                <GraduationCap className="w-5 h-5" />
                <span>Bitcoin einfach verstehen</span>
              </div>
              <p className="text-xl text-muted-foreground max-w-2xl mb-8">
                Dein Wegweiser durch die Welt von Bitcoin. Von den Grundlagen bis zu 
                fortgeschrittenen Themen wie Lightning Network und Dezentralisierung - 
                hier verstehst du endlich, worum es wirklich geht.
              </p>
              <div className="flex flex-wrap justify-center lg:justify-start gap-4">
                <Link
                  to="/kategorie/grundlagen"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-colors shadow-lg shadow-primary/25"
                >
                  Jetzt lernen
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/kategorie/geld"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-secondary text-secondary-foreground rounded-xl font-medium hover:bg-secondary/80 transition-colors"
                >
                  Was ist Geld?
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bitcoin-Duden Section */}
      <section className="container pt-16 pb-8">
        <Link to="/duden">
          <Card className="group border-2 border-primary/30 bg-gradient-to-r from-primary/5 to-primary/10 hover:border-primary hover:shadow-lg hover:shadow-primary/10 transition-all duration-300 overflow-hidden">
            <CardContent className="p-6 lg:p-8">
              <div className="flex items-center gap-4 lg:gap-6">
                <div className="w-14 h-14 lg:w-16 lg:h-16 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                  <BookOpen className="w-7 h-7 lg:w-8 lg:h-8 text-primary" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl lg:text-2xl font-bold group-hover:text-primary transition-colors">
                    Bitcoin-Duden
                  </h2>
                  <p className="text-muted-foreground">
                    Alle wichtigen Begriffe rund um Bitcoin einfach erklärt
                  </p>
                </div>
                <ArrowRight className="w-6 h-6 text-primary group-hover:translate-x-2 transition-transform" />
              </div>
            </CardContent>
          </Card>
        </Link>
      </section>

      {/* Categories Section */}
      <section className="container py-8">
        <h2 className="text-2xl font-bold mb-8">Themengebiete</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {CATEGORIES.map((category) => (
            <CategoryCard key={category.id} category={category} />
          ))}
        </div>
      </section>

      {/* Latest Articles Section */}
      <section className="container py-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl font-bold">Neueste Artikel</h2>
          <Link
            to="/kategorie/grundlagen"
            className="text-primary hover:underline font-medium flex items-center gap-1"
          >
            Alle Artikel
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <ArticleCardSkeleton key={i} />
            ))}
          </div>
        ) : articles && articles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        ) : (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center">
              <div className="max-w-md mx-auto space-y-4">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
                  <GraduationCap className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold">Bald verfügbar</h3>
                <p className="text-muted-foreground">
                  Die ersten Artikel werden bald veröffentlicht. Schau regelmäßig vorbei!
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </section>

      {/* CTA Section */}
      <section className="container pb-16">
        <Card className="bg-gradient-to-br from-primary/10 via-card to-card border-primary/20 overflow-hidden relative">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <CardContent className="p-8 lg:p-12 relative">
            <div className="max-w-2xl">
              <h2 className="text-2xl lg:text-3xl font-bold mb-4">
                Bereit, Bitcoin zu verstehen?
              </h2>
              <p className="text-muted-foreground mb-6">
                Jeder Artikel enthält am Ende ein Quiz, um dein Wissen zu testen. 
                So stellst du sicher, dass du die wichtigsten Konzepte wirklich verstanden hast.
              </p>
              <Link
                to="/kategorie/geld"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-medium hover:bg-primary/90 transition-colors shadow-lg shadow-primary/25"
              >
                Mit den Grundlagen starten
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Donation Section */}
      <DonationSection />
    </Layout>
  );
};

export default Index;
